import { OnScreenModal } from '@/components/ui/OnScreenModal';
import modalStyles from '@/features/application/styles/modals.module.css';
import type { PaymentAdjustmentModalProps } from '../../types/modals';
import { usePaymentAdjustmentModal } from '../../hooks/usePaymentAdjustmentModal';
import { PaymentAdjustmentCalculatorSection } from './PaymentAdjustmentCalculatorSection';
import { PaymentAdjustmentConfirmDialogs } from './PaymentAdjustmentConfirmDialogs';
import { PaymentAdjustmentNoteSection } from './PaymentAdjustmentNoteSection';
import { PaymentAdjustmentTypeSection } from './PaymentAdjustmentTypeSection';

export function PaymentAdjustmentModal({
  isOpen,
  onClose,
  onSubmit,
  onGenerateChargebackPacket,
  payment,
  application,
  autoSelectedData,
  requireNote = false,
}: PaymentAdjustmentModalProps) {
  const {
    tid,
    adjustmentType,
    submitting,
    handleInitialSubmit,
    typeSectionProps,
    calculatorSectionProps,
    noteSectionProps,
    confirmDialogsProps,
  } = usePaymentAdjustmentModal({
    onSubmit,
    onGenerateChargebackPacket,
    payment,
    application,
    autoSelectedData,
    requireNote,
  });

  if (!isOpen) return null;

  return (
    <>
      <OnScreenModal
        isOpen={true}
        title="Payment Process Adjustment"
        onClose={onClose}
        maxWidth="max-w-3xl"
        headerVariant="centeredTitle"
        bodyScrollOnly
        footer={
          <div
            className={modalStyles.modalFooterActionGroup}
            data-testid={`${tid}-footer`}
          >
            <button
              onClick={handleInitialSubmit}
              data-testid={`${tid}-submit-button`}
              disabled={submitting}
              className={modalStyles.modalActionPrimary}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              onClick={onClose}
              data-testid={`${tid}-cancel-btn`}
              disabled={submitting}
              className={modalStyles.modalActionSecondary}
            >
              Cancel
            </button>
          </div>
        }
        overlayDataTestId={tid}
        panelDataTestId={`${tid}-panel`}
        titleDataTestId={`${tid}-title`}
        closeButtonDataTestId={`${tid}-close-btn`}
      >
        <div className="space-y-6" data-testid={`${tid}-body`}>
          <div
            className={`grid gap-6 ${
              adjustmentType === 'chargeback' ? '' : 'lg:grid-cols-[1fr_1fr]'
            }`}
          >
            <PaymentAdjustmentTypeSection {...typeSectionProps} />

            {adjustmentType !== 'chargeback' && (
              <PaymentAdjustmentCalculatorSection
                {...calculatorSectionProps}
              />
            )}
          </div>

          <PaymentAdjustmentNoteSection {...noteSectionProps} />
        </div>
      </OnScreenModal>

      <PaymentAdjustmentConfirmDialogs {...confirmDialogsProps} />
    </>
  );
}
